"use client"

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { cn } from '@/shared/utils/tw'
import { ChatMessageList } from '@/entities/realtime-agent/ui/chat-message'
import { ChatMessageType } from '@/features/text-chat-with-agent/types/chat-message'

interface ChatOverlayProps {
  messages: ChatMessageType[]
  isLoading?: boolean
  isVisible: boolean
  className?: string
  background?: string
  isPreview?: boolean
  profileImageUrl?: string
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({
  messages,
  isLoading = false,
  isVisible,
  className,
  background = 'linear-gradient(180deg, #0A4766 6.28%, #011828 85.78%)',
  isPreview = false,
  profileImageUrl,
}) => {
  // 애니메이션을 위한 렌더링 상태 관리
  const [shouldRender, setShouldRender] = useState(isVisible)
  const [animationState, setAnimationState] = useState<'enter' | 'exit' | 'idle'>(
    isVisible ? 'enter' : 'idle'
  )

  // 자동 스크롤을 위한 ref
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // 스크롤을 맨 아래로 이동하는 함수
  const scrollToBottom = useCallback((instant = false) => {
    const el = scrollContainerRef.current
    if (el) {
      if (instant) {
        el.scrollTop = el.scrollHeight
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [])

  // 메시지 추가 또는 로딩 상태 변경 시 스크롤
  useEffect(() => {
    if (isVisible) {
      scrollToBottom(false)
    }
  }, [messages.length, isLoading, scrollToBottom])

  // 컨테이너 크기 변화 감지 (스트리밍으로 텍스트가 길어질 때)
  useEffect(() => {
    if (!isVisible || !messagesContainerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      scrollToBottom()
    })

    resizeObserver.observe(messagesContainerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isVisible, scrollToBottom])

  // 사용자 스크롤 시 스크롤바 표시 후 자동 숨김
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return

    let hideTimer: ReturnType<typeof setTimeout>

    const handleScroll = () => {
      el.classList.add('is-scrolling')
      clearTimeout(hideTimer)
      hideTimer = setTimeout(() => {
        el.classList.remove('is-scrolling')
      }, 1000)
    }

    el.addEventListener('scroll', handleScroll)
    return () => {
      el.removeEventListener('scroll', handleScroll)
      clearTimeout(hideTimer)
    }
  }, [shouldRender])

  useEffect(() => {
    if (isVisible) {
      // 보여질 때: 즉시 렌더링 + enter 애니메이션
      setShouldRender(true)
      setAnimationState('enter')
    } else if (shouldRender) {
      // 숨겨질 때: exit 애니메이션 후 렌더링 제거
      setAnimationState('exit')
    }
  }, [isVisible, shouldRender])

  // exit 애니메이션 완료 후 렌더링 제거
  const handleAnimationEnd = () => {
    if (animationState === 'exit') {
      setShouldRender(false)
      setAnimationState('idle')
    } else if (animationState === 'enter') {
      setAnimationState('idle')
      // enter 애니메이션 완료 후 맨 아래로 스크롤
      scrollToBottom(true)
    }
  }

  if (!shouldRender) return null

  return (
    <div
      className={cn(
        isPreview ? "absolute" : "fixed",
        "inset-0 z-[9] pointer-events-none w-full",
        isPreview ? "h-full" : "h-dvh",
        // 애니메이션 클래스 적용
        animationState === 'enter' && "animate-chat-enter",
        animationState === 'exit' && "animate-chat-exit",
        className
      )}
      style={{
        background,
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="relative w-full h-full">
        {/* 채팅 메시지 영역 */}
        <div className="absolute inset-x-0 top-[57px] bottom-[0px] pointer-events-auto">
          <div ref={scrollContainerRef} className="h-full overflow-y-auto px-4 pt-4 chat-scroll-container">
            <div ref={messagesContainerRef}>
              <ChatMessageList
                messages={messages}
                isLoading={isLoading}
                profileImageUrl={profileImageUrl}
              />
            </div>
            {/* 자동 스크롤을 위한 앵커 */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatOverlay
